import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BadgeCheck,
  BookOpen,
  Calendar,
  Download,
  ExternalLink,
  FileText,
  Filter,
  Globe,
  Grid3X3,
  Library,
  List,
  Search,
  Sparkles,
} from "lucide-react";
import { api } from "../../lib/api";
import { publicAssetUrl } from "../../lib/adminApi";

const fallbackIssues = [
  {
    id: "v3n2-2025",
    title: "Vol. 3, No. 2 (November 2025)",
    slug: "vol-3-no-2-november-2025",
    year: 2025,
    volume: "Vol. 3",
    issue: "No. 2",
    publication_date: "2025-11-01",
    issn: "2786-9822",
    eissn: "3043-5382",
    publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
    description: "A multidisciplinary collection of peer-reviewed research papers from humanities, science and technology.",
    cover_image: "/journal-covers/vol3-no2-nov-2025.jpeg",
    pdf_url: "/journals/BIDA POLYMATH Vol 3 Issue 2 Nov 2025.pdf",
    article_count: 0,
    is_featured: 1,
  },
  {
    id: "v3n1-2025",
    title: "Vol. 3, No. 1 (June 2025)",
    slug: "vol-3-no-1-june-2025",
    year: 2025,
    volume: "Vol. 3",
    issue: "No. 1",
    publication_date: "2025-06-01",
    issn: "2786-9822",
    eissn: "3043-5382",
    publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
    description: "Latest research issue containing scholarly papers and innovation-focused academic contributions.",
    cover_image: "/journal-covers/vol3-no1-june-2025.jpeg",
    pdf_url: "/journals/BIDA POLYMATH Vol 3 Issue 1 June 2025.pdf",
    article_count: 0,
  },
  {
    id: "v2n2-2024",
    title: "Vol. 2, No. 2 (December 2024)",
    slug: "vol-2-no-2-december-2024",
    year: 2024,
    volume: "Vol. 2",
    issue: "No. 2",
    publication_date: "2024-12-01",
    issn: "2786-9822",
    eissn: "3043-5382",
    publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
    description: "Research papers and journal contributions from institutional academic authors.",
    cover_image: "/journal-covers/vol2-no2-dec-2024.jpeg",
    pdf_url: "/journals/BIDA VOLUME 2, NUMBER 2, DECEMBER 2024.pdf",
    article_count: 0,
  },
  {
    id: "v2n1-2024",
    title: "Vol. 2, No. 1 (June 2024)",
    slug: "vol-2-no-1-june-2024",
    year: 2024,
    volume: "Vol. 2",
    issue: "No. 1",
    publication_date: "2024-06-01",
    issn: "2786-9822",
    eissn: "3043-5382",
    publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
    description: "Open access issue featuring multidisciplinary research and academic scholarship.",
    cover_image: "/journal-covers/vol2-no1-june-2024.jpeg",
    pdf_url: "/journals/BIDA VOLUME 2, NUMBER 1, JUNE 2024.pdf",
    article_count: 0,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.08 } },
};

function formatDate(value) {
  if (!value) return "Recently published";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function publicationYear(issue) {
  if (issue.year) return Number(issue.year);
  const parsed = new Date(issue.publication_date || issue.published_at || issue.created_at || "");
  if (!Number.isNaN(parsed.getTime())) return parsed.getFullYear();
  const match = String(issue.title || "").match(/20\d{2}/);
  return match ? Number(match[0]) : new Date().getFullYear();
}

function resolveAsset(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/journal-covers") || path.startsWith("/journals") || path.startsWith("/images")) return path;
  return publicAssetUrl(path);
}

function issuePdf(issue) {
  return issue.pdf_url || issue.issue_pdf || issue.download_url || "";
}

function issueSubtitle(issue) {
  return [issue.volume || "Volume", issue.issue || "Issue", formatDate(issue.publication_date)]
    .filter(Boolean)
    .join(" • ");
}

function shortJournalTitle(issue) {
  return issue.journal_title || "Polymath Journal of Humanities, Science & Technology";
}

function StatPill({ label, value, icon: Icon }) {
  return (
    <motion.div
      variants={fadeUp}
      className="rounded-2xl border border-white/15 bg-white/10 px-4 py-3 text-white shadow-2xl shadow-black/10 backdrop-blur-md"
    >
      <div className="flex items-center gap-2 text-white/70">
        {Icon ? <Icon className="h-4 w-4" /> : null}
        <span className="text-[11px] font-black uppercase tracking-[0.2em]">{label}</span>
      </div>
      <p className="mt-1 text-2xl font-black leading-none">{value}</p>
    </motion.div>
  );
}

function IssueCard({ issue, view }) {
  const pdf = issuePdf(issue);
  const cover = resolveAsset(issue.cover_image || issue.coverUrl);
  const card = (
    <motion.article
      variants={fadeUp}
      whileHover={{ y: -5 }}
      className={`group overflow-hidden rounded-[1.65rem] border border-slate-200 bg-white shadow-sm transition hover:shadow-2xl hover:shadow-blue-950/10 ${
        view === "list" ? "grid gap-0 md:grid-cols-[210px_1fr]" : ""
      }`}
    >
      <div className={`relative bg-slate-100 ${view === "list" ? "min-h-[220px]" : "aspect-[4/3]"}`}>
        {cover ? (
          <img src={cover} alt={issue.title} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-900 to-indigo-950 p-6 text-center text-lg font-black text-white">
            Journal Issue
          </div>
        )}
        <div className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-blue-800 shadow backdrop-blur">
          {publicationYear(issue)}
        </div>
        {Number(issue.is_featured) === 1 ? (
          <div className="absolute right-3 top-3 rounded-full bg-blue-600 px-3 py-1 text-[11px] font-black uppercase tracking-wider text-white shadow">
            Featured
          </div>
        ) : null}
      </div>
      <div className="p-5">
        <p className="text-[11px] font-black uppercase tracking-[0.18em] text-blue-600">Journal Issue</p>
        <h3 className="mt-2 line-clamp-2 text-lg font-black leading-tight text-slate-950">{issue.title}</h3>
        <p className="mt-2 text-xs font-semibold text-slate-500">{issueSubtitle(issue)}</p>
        <p className="mt-3 line-clamp-2 text-sm leading-6 text-slate-600">
          {issue.description || "Open this issue to view published papers, abstracts and downloadable journal materials."}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Link
            to={`/journals/${issue.slug || issue.id}`}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2 text-xs font-black text-white transition hover:bg-blue-800"
          >
            Open Issue <ExternalLink className="h-3.5 w-3.5" />
          </Link>
          {pdf ? (
            <a
              href={resolveAsset(pdf)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-black text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
            >
              Download <Download className="h-3.5 w-3.5" />
            </a>
          ) : null}
        </div>
      </div>
    </motion.article>
  );

  return card;
}

export default function ResearchPortal() {
  const [issues, setIssues] = useState([]);
  const [articles, setArticles] = useState([]);
  const [query, setQuery] = useState("");
  const [year, setYear] = useState("all");
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load(activeQuery = query) {
    setLoading(true);
    setError("");
    try {
      const params = activeQuery ? `?q=${encodeURIComponent(activeQuery)}` : "";
      const [issueData, articleData] = await Promise.all([
        api(`/api/journal-issues${params}`),
        api(`/api/journal-articles${params}`),
      ]);
      const cleanIssues = Array.isArray(issueData) ? issueData : [];
      const cleanArticles = Array.isArray(articleData) ? articleData : [];
      setIssues(cleanIssues.length ? cleanIssues : fallbackIssues);
      setArticles(cleanArticles);
    } catch (err) {
      setError("Live CMS records could not be loaded, so the classic journal archive preview is being shown.");
      setIssues(fallbackIssues);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allIssues = issues.length ? issues : fallbackIssues;

  const years = useMemo(() => {
    return ["all", ...Array.from(new Set(allIssues.map(publicationYear))).sort((a, b) => b - a)];
  }, [allIssues]);

  const filteredIssues = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allIssues
      .filter((issue) => (year === "all" ? true : publicationYear(issue) === Number(year)))
      .filter((issue) => {
        if (!q) return true;
        return [issue.title, issue.description, issue.volume, issue.issue, issue.issn, issue.eissn, issue.publisher]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => publicationYear(b) - publicationYear(a) || Number(b.is_featured || 0) - Number(a.is_featured || 0));
  }, [allIssues, query, year]);

  const featuredIssue = useMemo(() => {
    return filteredIssues.find((item) => Number(item.is_featured) === 1) || filteredIssues[0] || allIssues[0];
  }, [allIssues, filteredIssues]);

  const totalArticles = useMemo(() => {
    const counted = allIssues.reduce((sum, issue) => sum + Number(issue.article_count || 0), 0);
    return counted || articles.length || "Open";
  }, [allIssues, articles.length]);

  const heroCover = resolveAsset(featuredIssue?.cover_image || featuredIssue?.coverUrl);
  const heroPdf = featuredIssue ? issuePdf(featuredIssue) : "";
  const heroTitle = featuredIssue ? shortJournalTitle(featuredIssue) : "Polymath Journal of Humanities, Science & Technology";

  return (
    <main className="min-h-screen bg-[#f5f7fb] text-slate-950">
      <section className="relative overflow-hidden bg-[#101936] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(59,130,246,0.22),transparent_35%),radial-gradient(circle_at_90%_20%,rgba(168,85,247,0.2),transparent_32%),linear-gradient(135deg,#08111f,#11183a_45%,#1d1b4f)]" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.65) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.65) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <motion.div
          className="absolute -right-20 top-14 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl"
          animate={{ x: [0, -15, 10, 0], y: [0, 15, -8, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute -left-24 bottom-6 h-80 w-80 rounded-full bg-purple-600/20 blur-3xl"
          animate={{ x: [0, 12, -12, 0], y: [0, -10, 14, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        />

        <motion.div variants={stagger} initial="hidden" animate="show" className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-blue-100 backdrop-blur">
              <BadgeCheck className="h-4 w-4 text-blue-300" /> Peer-reviewed and open access
            </span>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white/70 backdrop-blur">
              <Globe className="h-4 w-4" /> Research & Publications
            </span>
          </motion.div>

          <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_420px] lg:items-end">
            <div>
              <motion.p variants={fadeUp} className="text-sm font-black uppercase tracking-[0.45em] text-blue-200">Federal Polytechnic Bida</motion.p>
              <motion.h1 variants={fadeUp} className="mt-4 max-w-4xl text-4xl font-black leading-[0.98] tracking-tight md:text-6xl lg:text-7xl">
                {heroTitle.split(" of ")[0]} <span className="bg-gradient-to-r from-blue-300 via-white to-purple-300 bg-clip-text text-transparent">of {heroTitle.split(" of ").slice(1).join(" of ") || "Humanities, Science & Technology"}</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="mt-5 max-w-3xl text-base leading-8 text-slate-200 md:text-lg">
                Explore journal issues, article abstracts, authors, keywords, publication metadata and downloadable research papers from the official institutional research portal.
              </motion.p>

              <motion.div variants={fadeUp} className="mt-7 flex flex-wrap gap-3 text-xs font-black uppercase tracking-wider text-white/80">
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">ISSN: {featuredIssue?.issn || "2786-9822"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">E-ISSN: {featuredIssue?.eissn || "3043-5382"}</span>
                <span className="rounded-full border border-white/15 bg-white/10 px-4 py-2">Open Access</span>
              </motion.div>

              <motion.div variants={fadeUp} className="mt-8 flex flex-col gap-3 sm:flex-row">
                {featuredIssue ? (
                  <Link to={`/journals/${featuredIssue.slug || featuredIssue.id}`} className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-6 py-4 text-sm font-black text-white shadow-lg shadow-blue-950/20 transition hover:bg-blue-500">
                    View Latest Issue <ExternalLink className="h-4 w-4" />
                  </Link>
                ) : null}
                {heroPdf ? (
                  <a href={resolveAsset(heroPdf)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 text-sm font-black text-slate-950 transition hover:bg-blue-50">
                    Download PDF <Download className="h-4 w-4" />
                  </a>
                ) : null}
              </motion.div>
            </div>

            {featuredIssue ? (
              <motion.div variants={fadeUp} className="relative rounded-[2rem] border border-white/15 bg-white/10 p-4 shadow-2xl shadow-black/20 backdrop-blur-xl">
                <div className="absolute -right-4 -top-4 rounded-2xl bg-blue-600 px-4 py-2 text-xs font-black uppercase tracking-wider text-white shadow-lg">Latest Issue</div>
                <div className="grid gap-4 sm:grid-cols-[150px_1fr] lg:grid-cols-1 xl:grid-cols-[150px_1fr]">
                  <div className="overflow-hidden rounded-2xl bg-slate-800 shadow-xl">
                    {heroCover ? (
                      <img src={heroCover} alt={featuredIssue.title} className="h-52 w-full object-cover" />
                    ) : (
                      <div className="flex h-52 items-center justify-center p-5 text-center text-lg font-black">Journal Cover</div>
                    )}
                  </div>
                  <div className="self-center">
                    <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-200">Now Available</p>
                    <h2 className="mt-2 text-2xl font-black leading-tight text-white">{featuredIssue.title}</h2>
                    <p className="mt-2 text-sm font-semibold text-slate-300">{issueSubtitle(featuredIssue)}</p>
                    <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-300">{featuredIssue.description || "Published issue ready for reading and download."}</p>
                  </div>
                </div>
              </motion.div>
            ) : null}
          </div>

          <motion.div variants={stagger} className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatPill icon={Library} label="Journal Issues" value={allIssues.length} />
            <StatPill icon={FileText} label="Research Papers" value={totalArticles} />
            <StatPill icon={Calendar} label="Latest Year" value={years.find((item) => item !== "all") || new Date().getFullYear()} />
            <StatPill icon={BookOpen} label="Access Type" value="Open" />
          </motion.div>
        </motion.div>
      </section>

      <section className="sticky top-0 z-20 border-y border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100">
            <Search className="h-5 w-5 shrink-0 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(query)}
              placeholder="Search by issue title, volume, year, ISSN or keyword..."
              className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-slate-800 outline-none placeholder:text-slate-400"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-xs font-black uppercase tracking-wider text-slate-600">
              <Filter className="h-4 w-4" />
              <select value={year} onChange={(e) => setYear(e.target.value)} className="bg-transparent outline-none">
                {years.map((item) => (
                  <option key={item} value={item}>{item === "all" ? "All Years" : item}</option>
                ))}
              </select>
            </label>
            <button onClick={() => setView("grid")} className={`rounded-2xl px-4 py-3 text-sm font-black transition ${view === "grid" ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} aria-label="Grid view">
              <Grid3X3 className="h-4 w-4" />
            </button>
            <button onClick={() => setView("list")} className={`rounded-2xl px-4 py-3 text-sm font-black transition ${view === "list" ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} aria-label="List view">
              <List className="h-4 w-4" />
            </button>
            <button onClick={() => load(query)} className="rounded-2xl bg-yellow-400 px-5 py-3 text-sm font-black text-slate-950 transition hover:bg-yellow-300">
              Search
            </button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        {error ? <div className="mb-6 rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm font-bold text-yellow-900">{error}</div> : null}
        <div className="mb-7 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-blue-700">
              <Sparkles className="h-4 w-4" /> Journal Archive
            </p>
            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 md:text-4xl">Available Issues</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
              Browse journal issues in the same official archive style, while still allowing admin to manage issues, covers, metadata and articles from the CMS.
            </p>
          </div>
          <p className="text-sm font-black text-slate-500">{loading ? "Loading..." : `${filteredIssues.length} issue(s) found`}</p>
        </div>

        {!loading && filteredIssues.length === 0 ? (
          <div className="rounded-[2rem] border border-slate-200 bg-white p-10 text-center shadow-sm">
            <h3 className="text-xl font-black text-slate-950">No issue found</h3>
            <p className="mt-2 text-slate-600">Try another keyword or clear the year filter.</p>
          </div>
        ) : null}

        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }} className={view === "grid" ? "grid gap-6 md:grid-cols-2 xl:grid-cols-3" : "grid gap-6"}>
          {filteredIssues.map((issue) => <IssueCard key={issue.id || issue.slug} issue={issue} view={view} />)}
        </motion.div>

        <div className="mt-14 grid gap-8 lg:grid-cols-[1fr_360px]">
          <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-600">Latest Articles</p>
                <h2 className="mt-2 text-2xl font-black text-slate-950">Recently Published Papers</h2>
              </div>
              <FileText className="h-8 w-8 text-blue-600" />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {articles.slice(0, 6).map((article) => (
                <Link key={article.id} to={`/journals/article/${article.slug}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-5 transition hover:-translate-y-1 hover:border-blue-200 hover:bg-blue-50">
                  <h3 className="line-clamp-2 font-black text-slate-950">{article.title}</h3>
                  <p className="mt-2 line-clamp-1 text-sm font-semibold text-slate-600">{article.authors}</p>
                  <p className="mt-3 text-xs font-black uppercase tracking-wider text-blue-700">{article.issue_title || "Journal Article"}</p>
                </Link>
              ))}
              {!loading && articles.length === 0 ? <p className="rounded-2xl bg-slate-50 p-5 text-slate-600 md:col-span-2">No article records are available yet. Add articles from the admin journal CMS.</p> : null}
            </div>
          </section>

          <aside className="rounded-[2rem] border border-slate-200 bg-slate-950 p-6 text-white shadow-sm md:p-8">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-blue-300">Journal Information</p>
            <h2 className="mt-3 text-2xl font-black">Publication Details</h2>
            <dl className="mt-6 space-y-4 text-sm">
              <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
                <dt className="text-slate-400">ISSN</dt>
                <dd className="font-black">{featuredIssue?.issn || "2786-9822"}</dd>
              </div>
              <div className="flex items-start justify-between gap-4 border-b border-white/10 pb-4">
                <dt className="text-slate-400">E-ISSN</dt>
                <dd className="font-black">{featuredIssue?.eissn || "3043-5382"}</dd>
              </div>
              <div className="border-b border-white/10 pb-4">
                <dt className="text-slate-400">Publisher</dt>
                <dd className="mt-1 font-black">{featuredIssue?.publisher || "The Federal Polytechnic, Bida"}</dd>
              </div>
              <div>
                <dt className="text-slate-400">Access</dt>
                <dd className="mt-1 font-black">Open Access / PDF Download</dd>
              </div>
            </dl>
          </aside>
        </div>
      </section>
    </main>
  );
}
